import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import Download from '../Download/Download';

const API_URL = process.env.REACT_APP_API_URL;

// Storage key for the session token
const TOKEN_KEY = 'company_download_token';

// ─────────────────────────────────────────────────────────────────────────────
// CompanyDownloadPortal
//
// Props:
//   companyName  {string}  — uppercase company key from URL (e.g. "AFDS")
//   companyDisplay {string} — human-readable name for display
//
// Flow:
//   1. Check sessionStorage for a valid (non-expired) token
//   2. If found → go straight to the file list
//   3. If not → show login form
//   4. On successful login → store token in sessionStorage → show file list
//   5. Logout clears the token and returns to the login form
//
// Security notes:
//   - Token is stored in sessionStorage (cleared when tab closes, not localStorage)
//   - Token is sent as Authorization: Bearer <token> on every download request
//   - The Download component must be updated to accept and forward the token
// ─────────────────────────────────────────────────────────────────────────────

const DownloadPortal = ({ companyName, companyDisplay }) => {
  const [token,      setToken]      = useState(null);
  const [authedUser, setAuthedUser] = useState('');
  const [checking,   setChecking]   = useState(true);  // checking sessionStorage on mount

  // Login form state
  const [username,  setUsername]  = useState('');
  const [password,  setPassword]  = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [showPass,  setShowPass]  = useState(false);

  // Password-reset flow: 'login' → 'forgot' (request code) → 'reset' (enter code)
  const [mode,        setMode]        = useState('login');
  const [resetEmail,  setResetEmail]  = useState('');
  const [resetCode,   setResetCode]   = useState('');
  const [resetPw,     setResetPw]     = useState('');
  const [resetPw2,    setResetPw2]    = useState('');
  const [info,        setInfo]        = useState('');

  const goToMode = (m) => { setMode(m); setError(''); setInfo(''); };

  // On mount: check if there's already a valid session token
  useEffect(() => {
    const stored = sessionStorage.getItem(TOKEN_KEY);
    if (stored) {
      try {
        // Decode the JWT payload (no verification — just checking expiry client-side)
        // Real security happens on the server; this just avoids a pointless round-trip
        const payload = JSON.parse(atob(stored.split('.')[1]));
        const nowSecs = Math.floor(Date.now() / 1000);

        if (payload.exp && payload.exp > nowSecs && payload.companyName === companyName) {
          setToken(stored);
          setAuthedUser(payload.username || '');
          setChecking(false);
          return;
        }
      } catch {
        // Malformed token — fall through to login
      }
      sessionStorage.removeItem(TOKEN_KEY);
    }
    setChecking(false);
  }, [companyName]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/auth/company-login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username:    username.trim(),
          password,
          companyName,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        sessionStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
        setAuthedUser(username.trim());
        setPassword('');  // clear password from memory immediately
      } else {
        // Show the server's error message, but cap it to avoid information leakage
        setError(data.error || 'Login failed. Please try again.');
      }
    } catch {
      setError('Unable to reach server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Step 1 of reset: request a code be emailed. Server always returns a generic
  // success, so we advance to the code-entry step regardless.
  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/auth/forgot-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: resetEmail.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setInfo(data.message || 'If that account exists, a reset code has been sent.');
        setMode('reset');
      } else {
        setError(data.error || 'Request failed. Please try again.');
      }
    } catch {
      setError('Unable to reach server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 of reset: submit the code + new password.
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (resetPw !== resetPw2) { setError('Passwords do not match.'); return; }
    if (resetPw.length < 8)   { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/auth/reset-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: resetEmail.trim(), code: resetCode.trim(), newPassword: resetPw }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResetCode(''); setResetPw(''); setResetPw2('');
        setMode('login');
        setInfo('Password updated. You can now sign in with your new password.');
      } else {
        setError(data.error || 'Reset failed. Please try again.');
      }
    } catch {
      setError('Unable to reach server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAuthedUser('');
    setUsername('');
  };

  // ── Loading check ────────────────────────────────────────────────────────────
  if (checking) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" style={{ color: 'var(--amber)' }} />
      </div>
    );
  }

  // ── Authenticated — show file list ───────────────────────────────────────────
  if (token) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span style={{ color: 'var(--fg)', fontSize: '0.875rem' }}>
            Signed in as <strong>{authedUser}</strong>
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleLogout}
          >
            Sign out
          </Button>
        </div>
        {/*
          Pass the token to Download so it can attach it to all fetch requests.
          You will need to update your Download component to accept and use this prop.
          See the note at the bottom of this file.
        */}
        <Download companyName={companyName} authToken={token} />
      </div>
    );
  }

  // ── Forgot password — request a code ─────────────────────────────────────────
  if (mode === 'forgot') {
    return (
      <div className="panel" style={{ maxWidth: '420px', margin: '0 auto' }}>
        <h4 style={{ color: 'var(--fg)', marginBottom: '1.5rem', fontWeight: 500 }}>
          Reset your password
        </h4>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        <Form onSubmit={handleForgotRequest} autoComplete="off">
          <Form.Group className="mb-4">
            <Form.Label style={{ color: 'var(--fg)' }}>Email address</Form.Label>
            <Form.Control
              type="email"
              placeholder="you@company.com"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              autoFocus
              autoComplete="email"
              disabled={loading}
              required
            />
            <Form.Text style={{ color: 'var(--muted)' }}>
              We'll email a 6-digit code to the address on file for your account.
            </Form.Text>
          </Form.Group>
          <Button type="submit" className="custom-button w-100" disabled={loading || !resetEmail}>
            {loading
              ? <><Spinner animation="border" size="sm" className="me-2" />Sending…</>
              : 'Send code'}
          </Button>
          <div className="text-center mt-3">
            <button type="button" onClick={() => goToMode('login')}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--amber-dark)',
                       cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>
              Back to sign in
            </button>
          </div>
        </Form>
      </div>
    );
  }

  // ── Reset password — enter code + new password ───────────────────────────────
  if (mode === 'reset') {
    return (
      <div className="panel" style={{ maxWidth: '420px', margin: '0 auto' }}>
        <h4 style={{ color: 'var(--fg)', marginBottom: '1.5rem', fontWeight: 500 }}>
          Enter your reset code
        </h4>
        {info  && <Alert variant="info">{info}</Alert>}
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        <Form onSubmit={handleResetSubmit} autoComplete="off">
          <Form.Group className="mb-3">
            <Form.Label style={{ color: 'var(--fg)' }}>6-digit code</Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="Enter the 6-digit code"
              value={resetCode}
              onChange={e => setResetCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              disabled={loading}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ color: 'var(--fg)' }}>New password</Form.Label>
            <Form.Control
              type="password"
              placeholder="New password (min 8 characters)"
              value={resetPw}
              onChange={e => setResetPw(e.target.value)}
              autoComplete="new-password"
              disabled={loading}
              required
            />
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label style={{ color: 'var(--fg)' }}>Confirm new password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Re-enter new password"
              value={resetPw2}
              onChange={e => setResetPw2(e.target.value)}
              autoComplete="new-password"
              disabled={loading}
              required
            />
          </Form.Group>
          <Button type="submit" className="custom-button w-100"
            disabled={loading || resetCode.length < 6 || !resetPw || !resetPw2}>
            {loading
              ? <><Spinner animation="border" size="sm" className="me-2" />Updating…</>
              : 'Set new password'}
          </Button>
          <div className="text-center mt-3">
            <button type="button" onClick={() => goToMode('forgot')}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--amber-dark)',
                       cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>
              Didn't get a code? Try again
            </button>
          </div>
        </Form>
      </div>
    );
  }

  // ── Not authenticated — show login form ──────────────────────────────────────
  return (
    <div className="panel" style={{ maxWidth: '420px', margin: '0 auto' }}>
      <h4 style={{ color: 'var(--fg)', marginBottom: '1.5rem', fontWeight: 500 }}>
        Sign in to access {companyDisplay} files
      </h4>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {info && (
        <Alert variant="success" onClose={() => setInfo('')} dismissible>
          {info}
        </Alert>
      )}

      <Form onSubmit={handleLogin} autoComplete="off">
        <Form.Group className="mb-3">
          <Form.Label style={{ color: 'var(--fg)' }}>Username</Form.Label>
          <Form.Control
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoFocus
            autoComplete="username"
            disabled={loading}
            required
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label style={{ color: 'var(--fg)' }}>Password</Form.Label>
          <div className="position-relative">
            <Form.Control
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={loading}
              required
            />
            <button
              type="button"
              onClick={() => setShowPass(v => !v)}
              style={{
                position:   'absolute',
                right:      '10px',
                top:        '50%',
                transform:  'translateY(-50%)',
                background: 'none',
                border:     'none',
                cursor:     'pointer',
                color:      'var(--muted)',
                padding:    0,
                fontSize:   '0.8rem',
              }}
              tabIndex={-1}
              aria-label={showPass ? 'Hide password' : 'Show password'}
            >
              {showPass ? 'Hide' : 'Show'}
            </button>
          </div>
        </Form.Group>

        <Button
          type="submit"
          className="custom-button w-100"
          disabled={loading || !username || !password}
        >
          {loading
            ? <><Spinner animation="border" size="sm" className="me-2" />Signing in…</>
            : 'Sign in'
          }
        </Button>

        <div className="text-center mt-3">
          {/* <button
            type="button"
            onClick={() => { setResetEmail(''); goToMode('forgot'); }}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: 'var(--amber-dark)', cursor: 'pointer', fontSize: '0.85rem',
              textDecoration: 'underline',
            }}
          >
            Forgot password?
          </button> */}
        </div>
      </Form>
    </div>
  );
};

export default DownloadPortal;

